import * as React from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Search, MoreVertical, Shield, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { RequireRole } from '@/lib/auth';
import { apiFetch } from '@/lib/api';

interface User {
  id: string;
  name: string;
  login: string;
  role: 'operador_caixa' | 'gerente' | 'administrador';
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface UserFormData {
  name: string;
  login: string;
  password: string;
  role: 'operador_caixa' | 'gerente' | 'administrador';
}

const ROLE_LABELS: Record<string, string> = {
  operador_caixa: 'Operador de Caixa',
  gerente: 'Gerente',
  administrador: 'Administrador',
};

const ROLE_VARIANTS: Record<string, 'default' | 'secondary' | 'warning'> = {
  operador_caixa: 'secondary',
  gerente: 'warning',
  administrador: 'default',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  operador_caixa: <Shield className="h-3 w-3" />,
  gerente: <ShieldCheck className="h-3 w-3" />,
  administrador: <ShieldAlert className="h-3 w-3" />,
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

function UsersPageContent() {
  const [users, setUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<User | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = React.useState<User | null>(null);
  const [formData, setFormData] = React.useState<UserFormData>({
    name: '',
    login: '',
    password: '',
    role: 'operador_caixa',
  });
  const [formErrors, setFormErrors] = React.useState<Partial<Record<keyof UserFormData, string>>>({});
  const [submitting, setSubmitting] = React.useState(false);

  const fetchUsers = React.useCallback(async () => {
    try {
      const response = await apiFetch(`/api/users?search=${encodeURIComponent(debouncedSearch)}&limit=100`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data);
      }
    } catch {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  function openCreateModal() {
    setEditingUser(null);
    setFormData({ name: '', login: '', password: '', role: 'operador_caixa' });
    setFormErrors({});
    setModalOpen(true);
  }

  function openEditModal(user: User) {
    setEditingUser(user);
    setFormData({ name: user.name, login: user.login, password: '', role: user.role });
    setFormErrors({});
    setModalOpen(true);
  }

  function validateForm(): boolean {
    const errors: Partial<Record<keyof UserFormData, string>> = {};
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    if (!formData.login.trim()) errors.login = 'Login é obrigatório';
    if (!editingUser && !formData.password) errors.password = 'Senha é obrigatória';
    if (formData.password && formData.password.length < 4) errors.password = 'Mínimo 4 caracteres';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (editingUser) {
        // Update
        const body: Record<string, string> = {
          name: formData.name,
          login: formData.login,
          role: formData.role,
        };
        if (formData.password) body.password = formData.password;

        const response = await apiFetch(`/api/users/${editingUser.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao atualizar');
        }
        toast.success('Usuário atualizado com sucesso');
      } else {
        // Create
        const response = await apiFetch('/api/users', {
          method: 'POST',
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao criar');
        }
        toast.success('Usuário criado com sucesso');
      }

      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleToggleStatus(user: User) {
    if (user.active) {
      setConfirmDeactivate(user);
    } else {
      doToggleStatus(user);
    }
  }

  async function doToggleStatus(user: User) {
    try {
      const response = await apiFetch(`/api/users/${user.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !user.active }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao alterar status');
      }

      toast.success(`Usuário ${!user.active ? 'ativado' : 'desativado'} com sucesso`);
      setConfirmDeactivate(null);
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    }
  }

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Nome',
      sortable: true,
      render: (row) => <span className="font-medium text-base">{row.name}</span>,
    },
    {
      key: 'login',
      header: 'Login',
      sortable: true,
      render: (row) => <span className="text-base">{row.login}</span>,
    },
    {
      key: 'role',
      header: 'Perfil',
      render: (row) => (
        <Badge variant={ROLE_VARIANTS[row.role] || 'default'}>
          {ROLE_ICONS[row.role]}
          {ROLE_LABELS[row.role] || row.role}
        </Badge>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.active ? 'success' : 'destructive'} dot>
          {row.active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEditModal(row)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label={`Editar ${row.name}`}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(row)}
            className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
              row.active
                ? 'text-destructive hover:bg-destructive/10'
                : 'text-success hover:bg-success/10'
            }`}
            aria-label={row.active ? `Desativar ${row.name}` : `Ativar ${row.name}`}
          >
            {row.active ? 'Desativar' : 'Ativar'}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Gestão de Usuários
          </h1>
          <p className="mt-1 text-base text-muted-foreground">
            Gerencie os usuários e permissões do sistema
          </p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome ou login..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-base placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={users}
          getRowKey={(row) => row.id}
          emptyMessage="Nenhum usuário encontrado"
        />
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        description={editingUser ? 'Atualize as informações do usuário' : 'Preencha os dados para criar um novo usuário'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="user-name" className="mb-1.5 block text-sm font-medium text-muted-foreground">
              Nome
            </label>
            <input
              id="user-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              className={`flex h-11 w-full rounded-lg border bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.name ? 'border-destructive' : 'border-input'}`}
            />
            {formErrors.name && <p className="mt-1 text-xs text-destructive">{formErrors.name}</p>}
          </div>

          <div>
            <label htmlFor="user-login" className="mb-1.5 block text-sm font-medium text-muted-foreground">
              Login
            </label>
            <input
              id="user-login"
              type="text"
              value={formData.login}
              onChange={(e) => setFormData((p) => ({ ...p, login: e.target.value }))}
              className={`flex h-11 w-full rounded-lg border bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.login ? 'border-destructive' : 'border-input'}`}
            />
            {formErrors.login && <p className="mt-1 text-xs text-destructive">{formErrors.login}</p>}
          </div>

          <div>
            <label htmlFor="user-password" className="mb-1.5 block text-sm font-medium text-muted-foreground">
              {editingUser ? 'Nova Senha (deixe vazio para manter)' : 'Senha'}
            </label>
            <input
              id="user-password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
              className={`flex h-11 w-full rounded-lg border bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.password ? 'border-destructive' : 'border-input'}`}
            />
            {formErrors.password && <p className="mt-1 text-xs text-destructive">{formErrors.password}</p>}
          </div>

          <div>
            <label htmlFor="user-role" className="mb-1.5 block text-sm font-medium text-muted-foreground">
              Perfil
            </label>
            <select
              id="user-role"
              value={formData.role}
              onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value as UserFormData['role'] }))}
              className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            >
              <option value="operador_caixa">Operador de Caixa</option>
              <option value="gerente">Gerente</option>
              <option value="administrador">Administrador</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Salvando...' : editingUser ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Confirm Deactivate Modal */}
      {confirmDeactivate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-foreground mb-2">Desativar Usuário</h3>
            <p className="text-base text-muted-foreground mb-4">
              Tem certeza que deseja desativar o usuário <strong>{confirmDeactivate.name}</strong>?
              Ele não poderá mais acessar o sistema.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmDeactivate(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => doToggleStatus(confirmDeactivate)}>
                Desativar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function UsersPage() {
  return (
    <RequireRole roles={['administrador']}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <UsersPageContent />
      </motion.div>
    </RequireRole>
  );
}
